import { useState, useEffect, useRef } from 'react';
import { generateSecretKey, getPublicKey } from 'nostr-tools/pure';
import { nip19 } from 'nostr-tools';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { PasswordInput } from '@/components/ui/password-input';
import { RefreshCw, Key, Globe } from 'lucide-react';

interface NsiteDeployFormProps {
  gateway: string;
  savedNsec?: string;
  onNsecChange: (nsec: string) => void;
}

export function NsiteDeployForm({
  gateway,
  savedNsec,
  onNsecChange,
}: NsiteDeployFormProps) {
  const [nsec, setNsec] = useState(savedNsec || '');
  const [npub, setNpub] = useState('');
  const initialized = useRef(false);

  // Initialize nsec on mount
  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      if (savedNsec) {
        // If we have a saved nsec, notify parent immediately
        onNsecChange(savedNsec);
      } else if (!nsec) {
        // Generate a new nsec if none exists
        const sk = generateSecretKey();
        const newNsec = nip19.nsecEncode(sk);
        setNsec(newNsec);
        onNsecChange(newNsec);
      }
    }
  }, [savedNsec, nsec, onNsecChange]);

  // Update npub when nsec changes
  useEffect(() => {
    if (nsec) {
      try {
        const decoded = nip19.decode(nsec);
        if (decoded.type === 'nsec') {
          const pk = getPublicKey(decoded.data);
          const npubEncoded = nip19.npubEncode(pk);
          setNpub(npubEncoded);
        }
      } catch {
        setNpub('');
      }
    }
  }, [nsec]);

  const handleNsecChange = (value: string) => {
    setNsec(value);
    onNsecChange(value);
  };

  const handleGenerateNew = () => {
    const sk = generateSecretKey();
    const newNsec = nip19.nsecEncode(sk);
    setNsec(newNsec);
    onNsecChange(newNsec);
  };

  const fullDomain = npub ? `${npub}.${gateway}` : `npub1...${gateway}`;

  return (
    <div className="space-y-4">
      {/* Site URL Preview */}
      <div className="rounded-lg border bg-card p-4 space-y-3">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Globe className="h-4 w-4 text-muted-foreground" />
          Site URL
        </div>
        <p className="text-xs font-mono bg-muted/50 p-2.5 rounded-md break-all text-muted-foreground">
          {fullDomain}
        </p>
        <p className="text-xs text-muted-foreground">
          Each site has a unique Nostr keypair that determines its URL
        </p>
      </div>

      {/* Private Key Input */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Key className="h-3.5 w-3.5 text-muted-foreground" />
          <Label className="text-sm font-medium">Site Private Key</Label>
        </div>
        <div className="flex gap-2">
          <PasswordInput
            value={nsec}
            onChange={(e) => handleNsecChange(e.target.value)}
            placeholder="nsec1..."
            className="flex-1 h-10 bg-background font-mono text-sm"
          />
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={handleGenerateNew}
            title="Generate new key"
            className="shrink-0 h-10 w-10"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Keep this private key secure. It's used to sign your site's metadata.
        </p>
      </div>
    </div>
  );
}
